// SalesTableDefault.js (Updated to show raw data)
import React from 'react';
import {
    Box, Typography, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TableFooter, CircularProgress
} from "@mui/material";

// Helper function to check if a header represents a numeric value (same as in parent)
const isNumericHeader = (header) => {
    if (header.toUpperCase() === 'SALES MAN') {
        return false; // Explicitly treat SALES MAN as non-numeric
    }

   return ['AMT', 'COMMISSION', 'SALES', 'RETURN', 'VAT', 'TOTAL', '%', 'AMOUNT', 'VALUE', 'QTY'].some(s => header.toUpperCase().includes(s));
}

const SalesTableDefault = ({ rows, mode, grandColumnTotals, loading, formatNumber }) => {

    const columns = rows.length > 0 ? Object.keys(rows[0]) : [];
    const totalColumns = columns.length;

    // Fallback display for when no data is found
    if (!loading && rows.length === 0) {
        return (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="subtitle1" color="textSecondary">No sales data found for the selected filters.</Typography>
                <Typography variant="caption" display="block" sx={{ mt: 1, color: '#d32f2f' }}>
                    Current Report Mode: {mode}
                </Typography>
            </Box>
        );
    }

    // Filter out internal/helper keys from columns to display
    const visibleColumns = columns.filter(key =>
        !['isGroupHeader', 'isSubHeader', 'isSubSubHeader', 'totalAmount', 'totalAmountExclVAT', 'CASHCRED'].includes(key)
    );

    //start

    const calculateFilteredTotals = () => {
        if (rows.length === 0) return {};

        // Identify only the numeric columns that should be totaled
        const numericKeys = Object.keys(rows[0]).filter(key =>
            // 1. Must be a visible column
            visibleColumns.includes(key) &&
            // 2. Must be defined as a numeric header
            isNumericHeader(key)
        );

        const totals = {};

        // Initialize totals and sum the values from the filtered rows
        for (const key of numericKeys) {
            totals[key] = rows.reduce((sum, row) => sum + (Number(row[key]) || 0), 0);
        }

        return totals;
    };

    // Calculate the totals based on the current 'rows' prop
    const dynamicGrandTotals = calculateFilteredTotals();
    //end

    // Filter grand totals to only include keys present in the visible columns
    const footerKeys = Object.keys(dynamicGrandTotals).filter(key => visibleColumns.includes(key));
    const descriptiveSpan = visibleColumns.length - footerKeys.length;


    const renderHeader = () => (
        <TableHead>
            <TableRow>
                {visibleColumns.map(key => (
                    <TableCell
                        key={key}
                        align={isNumericHeader(key) ? 'right' : 'left'}
                        style={{ minWidth: 100, fontWeight: 'bold' }}
                    >
                        {key}
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );

    const renderBody = () => (
        <TableBody>
            {rows.map((row, index) => (
                <TableRow key={`default-row-${index}`} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                    {visibleColumns.map(key => {
                        const cellValue = row[key];
                        return (
                            <TableCell
                                key={key}
                                align={isNumericHeader(key) ? 'right' : 'left'}
                            >
                                {isNumericHeader(key) ? formatNumber(cellValue) : cellValue}
                            </TableCell>
                        );
                    })}
                </TableRow>
            ))}

        </TableBody>
    );

    // const renderFooter = () => (
    //     <TableFooter>
    //         <TableRow sx={{ backgroundColor: '#b3b3b3', borderTop: '3px double #000' }}>
    //             <TableCell
    //                 colSpan={descriptiveSpan}
    //                 align="right"
    //                 sx={{ fontWeight: 'bold', fontSize: '1.1em', padding: '8px 16px', position: 'sticky', bottom: 0, zIndex: 10, backgroundColor: '#b3b3b3' }}
    //             >
    //                 GRAND TOTAL:
    //             </TableCell>
    //             {footerKeys.map(key => (
    //                 <TableCell
    //                     key={key}
    //                     align="right"
    //                     sx={{ fontWeight: 'bold', fontSize: '1.1em' }}
    //                 >
    //                     {formatNumber(dynamicGrandTotals[key])}
    //                 </TableCell>
    //             ))}

               
    //         </TableRow>
    //     </TableFooter>
    // );

    const renderFooter = () => {
        const totalKeys = Object.keys(dynamicGrandTotals);
        const firstTotalKey = totalKeys.length > 0 ? totalKeys[0] : null;
        
        // grandTotalLabelSpan is the number of columns to span (7 for SALES MAN)
        const grandTotalLabelSpan = firstTotalKey 
            ? visibleColumns.indexOf(firstTotalKey)
            : visibleColumns.length;
    
        // We will use this flag to know when to start rendering the total/gap cells
        let firstTotalCellRendered = false; 
        let labelCellRendered = false;
    
        return (
            <TableFooter>
                <TableRow sx={{ backgroundColor: '#b3b3b3', borderTop: '3px double #000' }}>
                    
                    {visibleColumns.map((key, index) => {
                        
                        // --- 1. RENDER THE GRAND TOTAL LABEL CELL ---
                        // This cell covers columns 0 through 6 (SALES MAN)
                        if (!labelCellRendered) {
                            labelCellRendered = true; // Set the flag immediately
    
                            return (
                                <TableCell
                                    key="grand-total-label"
                                    colSpan={grandTotalLabelSpan}
                                    align="right"
                                    sx={{ fontWeight: 'bold', fontSize: '1.1em', padding: '8px 16px', position: 'sticky', bottom: 0, zIndex: 10, backgroundColor: '#b3b3b3' }}
                                >
                                    GRAND TOTAL:
                                </TableCell>
                            );
                        }
    
                        // --- 2. SKIP THE COLUMNS COVERED BY THE SPAN ---
                        // The label cell already occupied the space of columns 0 up to (grandTotalLabelSpan - 1).
                        // We must skip rendering cell content for these indices.
                        if (index < grandTotalLabelSpan) {
                             // The map continues to run for indices 0, 1, ..., 6. We skip rendering content for them.
                             return null;
                        }
    
                        // --- 3. RENDER THE TOTALS AND GAPS (Starts at index 7: 'AMOUNT') ---
                        if (index >= grandTotalLabelSpan) {
                            const isTotalColumn = dynamicGrandTotals.hasOwnProperty(key);
    
                            if (isTotalColumn) {
                                // Render total for AMOUNT, VATAMT, etc.
                                return (
                                    <TableCell key={key} align="right"
                                        sx={{ fontWeight: 'bold', fontSize: '1.1em', position: 'sticky', bottom: 0, zIndex: 10, backgroundColor: '#b3b3b3' }}>
                                        {formatNumber(dynamicGrandTotals[key])}
                                    </TableCell>
                                );
                            } else {
                                // Render empty cell for gaps like TYPE and ACCOUNT
                                return (
                                    <TableCell key={key} align="left" sx={{ backgroundColor: '#b3b3b3' }} />
                                );
                            }
                        }
                        
                        return null;
                    })}
                </TableRow>
            </TableFooter>
        );
    };


    return (
        <Box sx={{ p: 2 }}>

            <TableContainer sx={{ maxHeight: 'calc(100vh - 350px)', overflowY: 'auto', width: '100%' }}>
                <Table stickyHeader aria-label="default sales report">
                    {renderHeader()}
                    {renderBody()}
                    {rows.length > 0 && footerKeys.length > 0 && renderFooter()}
                </Table>

                {loading && (
                    <div style={{ display: "flex", justifyContent: 'center', alignItems: "center", width: "100%", height: "100%", position: "absolute", zIndex: 100, backgroundColor: "#ffffff80", top: "0px", left: "0px" }}>
                        <CircularProgress size={48} />
                    </div>
                )}

            </TableContainer>

        </Box>
    );
};

export default SalesTableDefault;